<template>
    <div>
        <div class="container max-w-3xl mx-auto">
            <div class="pt-4 pb-16 xl:pt-8 md:pb-20 xl:pb-24">
                <div class="md:text-center">
                    <h1 class="max-w-lg mx-auto mb-4 text-3xl font-semibold leading-tight tracking-tight font-noto-sans xs:text-4xl md:text-5xl lg:text-6xl text-grey-darkest">
                        We're making renting<br>
                        <span class="text-blue">better for tenants</span>
                    </h1>

                    <h2 class="max-w-md mx-auto mb-6 lg:mb-12 md:text-xl xl:text-2xl">
                        Pay your rent online, build your credit history, find your perfect home, view inventories and more.
                    </h2>
                </div>

                <div class="max-w-sm mx-auto">
                    <div class="flex flex-col justify-center mb-4 md:flex-row">
                        <laroute-link
                            :to="{
                                name: 'auth.users.create'
                            }"
                            class="flex-auto mb-3 shadow-md md:mb-0 md:mr-3 btn btn-blue btn-xl"
                        >
                            Get started
                        </laroute-link>

                        <laroute-link
                            :to="{
                                name: 'invitations.create'
                            }"
                            class="flex-auto shadow-md btn btn-white btn-xl"
                        >
                            Invite your landlord
                        </laroute-link>
                    </div>

                    <router-link
                        :to="{ name: 'www.landlords.index' }"
                        class="flex items-center font-semibold text-blue"
                    >
                        Are you a landlord?

                        <base-svg
                            name="icon-cheveron-right"
                            class="flex-none w-6 h-6 fill-primary-blue"
                        />
                    </router-link>
                </div>
            </div>

            <grid-layout class="md:pb-6 lg:pb-16 xl:pb-20">
                <grid-layout-item class="mb-12 lg:mb-0 lg:w-1/3">
                    <div class="inline-block p-3 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                        <base-svg
                            name="icon-card"
                            class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                        />
                    </div>

                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                        Flexible online payments
                    </h6>

                    <p>
                        Make flexible online payments with your credit or debit card - at any time. Pay daily, weekly, monthly or a combination of all three. Whatever works for you.
                    </p>
                </grid-layout-item>

                <grid-layout-item class="mb-12 lg:mb-0 lg:w-1/3">
                    <div class="inline-block p-3 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                        <base-svg
                            name="icon-tune"
                            class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                        />
                    </div>

                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                        All-in-one solution
                    </h6>

                    <p>
                        Pay your rent online, improve your credit score, find a new home, view inventories and more - anywhere, on any device, 24/7.
                    </p>
                </grid-layout-item>

                <grid-layout-item class="mb-12 lg:mb-0 lg:w-1/3">
                    <div class="inline-block p-3 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                        <base-svg
                            name="icon-user-group"
                            class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                        />
                    </div>

                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                        Find your perfect home
                    </h6>

                    <p>
                        Looking to move? Want to use Rentora? Landlords and agents who use Rentora
                        <router-link
                            :to="{
                                name: 'listings.index'
                            }"
                            class="text-blue"
                        >
                            list their properties
                        </router-link>
                        with us.
                    </p>
                </grid-layout-item>
            </grid-layout>
        </div>

        <svg
            class="w-full h-10 text-blue-bg"
            preserveAspectRatio="none"
            viewBox="0 0 375 40"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M375 40V0s-87.046 32-187.5 32S0 0 0 0v40h375z" />
        </svg>

        <div class="bg-blue-bg">
            <div class="container max-w-3xl pt-16 mx-auto xl:pt-32">
                <grid-layout class="flex-col-reverse items-center lg:flex-row">
                    <grid-layout-item class="lg:w-1/2">
                        <h3 class="mb-4 text-2xl font-semibold leading-tight tracking-tight xs:text-3xl font-noto-sans">
                            <router-link
                                :to="{
                                    name: 'www.tenants.payments.index'
                                }"
                                class="flex items-center text-blue"
                            >
                                Card payments

                                <base-svg
                                    name="icon-cheveron-right"
                                    class="w-8 h-8 fill-primary-blue"
                                />
                            </router-link>
                        </h3>

                        <p class="mb-12 text-lg lg:text-xl">
                            Pay rent your way - on your terms. We've brought paying rent into the 21st century
                        </p>

                        <div class="flex flex-col items-start mb-12 md:flex-row">
                            <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                <base-svg
                                    name="icon-card"
                                    class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                />
                            </div>

                            <div class="flex-grow md:pl-6">
                                <div class="mb-1 text-sm uppercase text-grey-dark">
                                    Online payments
                                </div>

                                <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                    Pay with a credit or debit card
                                </h6>

                                <p>
                                    Use your credit or debit card to pay your rent - it’s as easy as buying something off Amazon.
                                    All the major card brands are accepted.
                                </p>
                            </div>
                        </div>

                        <div class="flex flex-col items-start mb-12 md:flex-row">
                            <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                <base-svg
                                    name="icon-certificate"
                                    class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                />
                            </div>

                            <div class="flex-grow md:pl-6">
                                <div class="mb-1 text-sm uppercase text-grey-dark">
                                    Credit reporting
                                </div>

                                <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                    Build your credit history
                                </h6>

                                <p>
                                    We’ve partnered with Experian to make sure your rent payments are included in your credit history.
                                </p>
                            </div>
                        </div>

                        <div class="flex flex-col items-start md:flex-row">
                            <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                <base-svg
                                    name="icon-inbox"
                                    class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                />
                            </div>

                            <div class="flex-grow md:pl-6">
                                <div class="mb-1 text-sm uppercase text-grey-dark">
                                    Flexible payments
                                </div>

                                <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                    Pay when it suits you
                                </h6>

                                <p class="mb-8">
                                    Want to pay your rent weekly? Daily? Want to pay £50 tonight while watching a bit of telly?
                                    With Rentora, you can make payments online any time - on any device.
                                </p>

                                <router-link
                                    :to="{ name: 'www.tenants.payments.index' }"
                                    class="flex items-center text-blue"
                                >
                                    See more payment features

                                    <base-svg
                                        name="icon-cheveron-right"
                                        class="w-6 h-6 fill-primary-blue"
                                    />
                                </router-link>
                            </div>
                        </div>
                    </grid-layout-item>
                    <grid-layout-item class="mb-6 lg:mb-0 lg:w-1/2">
                        <img
                            src="/img/payment-form.svg"
                            class="w-full mx-auto"
                            :style="{
                                'max-width': '410px'
                            }"
                        >
                    </grid-layout-item>
                </grid-layout>
            </div>

            <div
                class="pt-24 bg-center bg-no-repeat lg:pt-48"
                :style="{
                    'background-image': 'url(/img/misc-background.svg)',
                    'background-position': 'top 50px left -50px',
                    'background-size': '462px 600px'
                }"
            >
                <div class="container max-w-3xl mx-auto">
                    <grid-layout class="flex-col-reverse items-center lg:flex-row-reverse">
                        <grid-layout-item class="lg:w-1/2">
                            <h3 class="mb-4 text-2xl font-semibold leading-tight tracking-tight xs:text-3xl font-noto-sans">
                                <router-link
                                    :to="{ name: 'www.tenants.organisation.index' }"
                                    class="flex items-center text-blue"
                                >
                                    Stay organised

                                    <base-svg
                                        name="icon-cheveron-right"
                                        class="w-8 h-8 fill-primary-blue"
                                    />
                                </router-link>
                            </h3>

                            <p class="mb-12 text-lg lg:text-xl">
                                There's lots of boring stuff related to renting. Don't worry though, we'll handle it.
                            </p>

                            <div class="flex flex-col items-start mb-12 md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-camera"
                                        class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Inventory
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Leave with your deposit
                                    </h6>

                                    <p>
                                        Rentora's inventory tool securely stores photos in high quality - backed up with descriptions.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col items-start mb-12 md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-calendar-date"
                                        class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Calendar
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Less stress, more sleep
                                    </h6>

                                    <p class="mb-3">
                                        We maintain a calendar for you, where you can see upcoming rents, tenancy terms, expiring documents and a lot more.
                                    </p>

                                    <p>
                                        You’ll receive reminders in a timely manner too - so you definitely won’t forget.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col items-start md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-notification"
                                        class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Notifications
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Never miss a thing
                                    </h6>

                                    <p class="mb-8">
                                        You’ll be kept in the loop with everything via email and text. Quite frankly, you’ll rarely need to visit your Rentora dashboard.
                                    </p>

                                    <router-link
                                        :to="{ name: 'www.landlords.organisation.index' }"
                                        class="flex items-center text-blue"
                                    >
                                        See more organisational features

                                        <base-svg
                                            name="icon-cheveron-right"
                                            class="w-6 h-6 fill-primary-blue"
                                        />
                                    </router-link>
                                </div>
                            </div>
                        </grid-layout-item>
                        <grid-layout-item class="mb-6 lg:mb-0 lg:w-1/2">
                            <img
                                src="/img/calendar.svg"
                                class="w-full mx-auto"
                                :style="{
                                    'max-width': '480px'
                                }"
                            >
                        </grid-layout-item>
                    </grid-layout>
                </div>
            </div>

            <div
                class="py-24 bg-center bg-no-repeat lg:py-48"
                :style="{
                    'background-image': 'url(/img/misc-background.svg)',
                    'background-position': 'top 75px right 50px',
                    'background-size': '462px 600px'
                }"
            >
                <div class="container max-w-3xl mx-auto">
                    <grid-layout class="flex-col-reverse items-center lg:flex-row">
                        <grid-layout-item class="lg:w-1/2">
                            <h3 class="mb-4 text-2xl font-semibold leading-tight tracking-tight xs:text-3xl font-noto-sans">
                                <router-link
                                    :to="{ name: 'www.tenants.listings.index' }"
                                    class="flex items-center text-blue"
                                >
                                    Property listings

                                    <base-svg
                                        name="icon-cheveron-right"
                                        class="w-8 h-8 fill-primary-blue"
                                    />
                                </router-link>
                            </h3>

                            <p class="mb-12 text-lg lg:text-xl">
                                Find your perfect home. Landlords and agents who use Rentora list their properties with us
                            </p>

                            <div class="flex flex-col items-start mb-12 md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-inbox"
                                        class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Online Applications
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Direct applications
                                    </h6>

                                    <p>
                                        Apply online quickly and easily. Applications are sent directly to the landlord or agent of the property. No middle man, no fuss.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col items-start md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-user-group"
                                        class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Co-Applicants
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Apply together
                                    </h6>

                                    <p class="mb-8">
                                        Renting with a partner/friend/roommate? Add them as a co-applicant and they'll receive an invitation to fill out their portion of the application.
                                    </p>

                                    <router-link
                                        :to="{ name: 'www.tenants.listings.index' }"
                                        class="flex items-center text-blue"
                                    >
                                        See more listing features

                                        <base-svg
                                            name="icon-cheveron-right"
                                            class="w-6 h-6 fill-primary-blue"
                                        />
                                    </router-link>
                                </div>
                            </div>
                        </grid-layout-item>
                        <grid-layout-item class="mb-6 lg:mb-0 lg:w-1/2">
                            <img
                                src="/img/property-listing.svg"
                                class="w-full mx-auto"
                                :style="{
                                    'max-width': '410px'
                                }"
                            >
                        </grid-layout-item>
                    </grid-layout>
                </div>
            </div>
        </div>

        <div class="container max-w-3xl mx-auto">
            <div class="pt-24 pb-12">
                <grid-layout>
                    <grid-layout-item class="lg:w-1/2">
                        <div class="flex mb-12">
                            <base-svg
                                name="icon-help-circle"
                                class="flex-none w-6 h-6 mr-6 fill-primary-green fill-secondary-green-lighter"
                            />

                            <div class="flex-grow">
                                <p class="mb-2 font-semibold font-noto-sans font-grey-darkest">
                                    Who are you?
                                </p>

                                <p>
                                    We're a team dedicated to reducing your costs and workload.
                                </p>
                            </div>
                        </div>
                    </grid-layout-item>

                    <grid-layout-item class="lg:w-1/2">
                        <div class="flex mb-12">
                            <base-svg
                                name="icon-help-circle"
                                class="flex-none w-6 h-6 mr-6 fill-primary-green fill-secondary-green-lighter"
                            />

                            <div class="flex-grow">
                                <p class="mb-2 font-semibold font-noto-sans font-grey-darkest">
                                    How much does it cost?
                                </p>

                                <p class="mb-3">
                                    For tenants - absolutely nothing.
                                </p>

                                <p class="mb-3">
                                    For landlords/agents, see the
                                    <router-link
                                        :to="{
                                            name: 'www.pricing.index'
                                        }"
                                        class="text-blue"
                                    >
                                        pricing page
                                    </router-link>
                                    for details and more FAQs specifically for pricing.
                                </p>
                            </div>
                        </div>
                    </grid-layout-item>
                </grid-layout>
            </div>

            <div class="flex flex-col items-center max-w-xl p-3 mx-auto mb-24 rounded lg:flex-row bg-blue-lightest xs:p-6 lg:p-12">
                <div class="mb-6 lg:mb-0 lg:w-1/2">
                    <h6 class="text-2xl font-semibold md:text-3xl font-noto-sans text-blue-darkest">
                        Ready to get started?
                    </h6>

                    <p class="text-lg lg:text-xl">
                        Create an account in a couple of minutes
                    </p>
                </div>

                <div class="flex flex-col justify-center w-full lg:w-1/2 md:flex-row lg:justify-end">
                    <laroute-link
                        :to="{
                            name: 'auth.users.create'
                        }"
                        class="w-full mb-3 shadow-md md:mb-0 md:mr-3 md:w-auto btn btn-blue-darkest btn-xl"
                    >
                        Get started
                    </laroute-link>

                    <laroute-link
                        :to="{
                            name: 'invitations.create'
                        }"
                        class="w-full shadow-md md:w-auto btn btn-white btn-xl"
                    >
                        Invite your landlord
                    </laroute-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import HeadMixin from '../../../mixins/head'

    export default {
        mixins: [
            HeadMixin
        ],
        mounted () {
            if (this.$route.hash) {
                setTimeout(() => {
                    location.href = this.$route.hash
                }, 1)
            }
        },
        methods: {
            head () {
                return {
                    title: [
                        'Rentora for tenants',
                        'Pay your rent online with a credit card'
                    ]
                }
            }
        }
    }
</script>
